import React, { useRef, useEffect, useState } from 'react'
import { Printer, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import type { SalaryById } from '@/api/types/payroll.types'
import { formatCurrency } from '@/lib/format'
import { getBranchCurrencyWords } from '@/lib/currency'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function formatMonthYearLabel(ym?: string | null): string {
  if (!ym || ym.length < 6) return '—'
  const parts = String(ym).split('-')
  let monthNum: number
  let yearStr: string
  if (parts.length >= 2 && parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else if (parts.length >= 2 && parts[1].length === 4) {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  } else {
    return ym
  }
  if (monthNum >= 1 && monthNum <= 12) return `${MONTH_NAMES[monthNum - 1]} ${yearStr}`
  return ym
}

function formatNum(n?: number | null): string {
  return formatCurrency(Number(n ?? 0))
}

function formatJoiningDate(d?: string | null): string {
  if (!d) return '—'
  try {
    const date = new Date(d)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return String(d).slice(0, 10)
  }
}

/** Simple number to words for amount in SAR (e.g. "Three Thousand Nine Hundred Thirty") */
function amountInWords(num: number): string {
  if (num <= 0 || !Number.isFinite(num)) return 'Zero'
  const intPart = Math.floor(num)
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']

  function toWords(n: number): string {
    if (n === 0) return ''
    if (n < 10) return ones[n]
    if (n < 20) return teens[n - 10]
    if (n < 100) return (tens[Math.floor(n / 10)] + ' ' + ones[n % 10]).trim()
    if (n < 1000) return (ones[Math.floor(n / 100)] + ' Hundred ' + toWords(n % 100)).trim()
    if (n < 1000000) return (toWords(Math.floor(n / 1000)) + ' Thousand ' + toWords(n % 1000)).trim()
    if (n < 1000000000) return (toWords(Math.floor(n / 1000000)) + ' Million ' + toWords(n % 1000000)).trim()
    return (toWords(Math.floor(n / 1000000000)) + ' Billion ' + toWords(n % 1000000000)).trim()
  }

  const str = toWords(intPart).trim() || 'Zero'
  return str + ' in ' + getBranchCurrencyWords().major
}

const TABLE_HEADER_CLASS = 'bg-primary text-primary-foreground text-left py-2 px-3 text-sm font-medium'
const TABLE_CELL_CLASS = 'border border-gray-200 py-2 px-3 text-sm text-gray-800'

export interface PaySlipModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: SalaryById | null
  loading?: boolean
  onPrint?: () => void
}

export interface EnrichedEmpInfo {
  employeeName?: string
  departmentName?: string
  nationalityName?: string
  joiningDate?: string
  reference?: string
  project?: string
}

export const PaySlipModal: React.FC<PaySlipModalProps> = ({ open, onOpenChange, data, loading, onPrint }) => {
  const printRef = useRef<HTMLDivElement>(null)
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const branchName = user?.selectedBranch?.branchName ?? 'Branch'
  const [empInfo, setEmpInfo] = useState<EnrichedEmpInfo | null>(null)

  useEffect(() => {
    if (!open || !data?.empId || !branchId) {
      setEmpInfo(null)
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const [employee, departments, countries] = await Promise.all([
          employeesService.getEmployeeById(branchId, data.empId!),
          employeesService.getDepartmentLookups(),
          employeesService.getCountryLookups(),
        ])
        if (cancelled) return
        const firstName = employee.empFirstNameEn ?? ''
        const secondName = employee.empSecondNameEn ?? ''
        const lastName = employee.empLastNameEn ?? ''
        const employeeName = [firstName, secondName, lastName].filter(Boolean).join(' ').trim() || undefined
        const deptId = employee.empDepartmetId ?? (employee as any).empDepartmentId
        const departmentName = deptId ? (departments.find((d) => d.id === deptId)?.lookupNameEn ?? (employee as any).departmentName) : undefined
        const nationalityId = employee.empNationalityId
        const nationalityName = nationalityId ? (countries.find((c) => c.id === nationalityId)?.lookupNameEn ?? (employee as any).nationalityName) : undefined
        setEmpInfo({
          employeeName: employeeName ?? (data as any).employeeName,
          departmentName: departmentName ?? (data as any).department,
          nationalityName: nationalityName ?? (data as any).nationality,
          joiningDate: employee.joiningDate ?? (data as any).joiningDate,
          reference: employee.empRefrence ?? (employee as any).empReferenceId ?? (data as any).reference,
          project: (data as any).project ?? (employee as any).projectName,
        })
      } catch {
        if (!cancelled) setEmpInfo(null)
      }
    }
    run()
    return () => { cancelled = true }
  }, [open, data?.empId, branchId])

  const handlePrint = () => {
    if (onPrint) {
      onPrint()
      return
    }
    const content = printRef.current
    if (!content) return

    const clone = content.cloneNode(true) as HTMLElement
    clone.querySelectorAll('img').forEach((node) => {
      const img = node as HTMLImageElement
      img.src = img.currentSrc || img.src
    })
    clone.querySelectorAll('.pay-slip-signature-footer').forEach((node) => {
      ;(node as HTMLElement).style.display = 'none'
    })

    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText = 'position:absolute;left:-9999px;top:0;width:210mm;height:297mm;border:0;'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument || iframe.contentWindow?.document
    if (!doc) {
      iframe.remove()
      return
    }

    doc.open()
    doc.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8" />
      <title>Pay Slip</title>
      <style>
        :root { --primary: 225 46% 16%; --primary-foreground: 0 0% 100%; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: #fff; color: #111827; font-family: system-ui, sans-serif; }
        .pay-slip-print { padding: 8px; background: #fff; color: #111827; }
        .pay-slip-header { display: flex; flex-direction: column; width: 100%; }
        .pay-slip-header-row { display: flex; justify-content: space-between; align-items: center; width: 100%; gap: 16px; margin-bottom: 12px; }
        .pay-slip-date { font-size: 12px; color: #6b7280; }
        .pay-slip-branch { flex: 1; text-align: center; font-size: 14px; font-weight: 600; color: #1f2937; }
        .pay-slip-logo-wrap { display: flex; justify-content: flex-end; }
        .pay-slip-logo-wrap img { height: 32px; max-height: 32px; object-fit: contain; }
        .pay-slip-title { text-align: center; font-size: 20px; font-weight: 600; color: #111827; margin: 8px 0; }
        .pay-slip-line { height: 3px; width: 100%; background: hsl(var(--primary)); margin: 0 0 24px 0; }
        .pay-slip-body { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
        table { border-collapse: collapse; width: 100%; }
        th { background: hsl(var(--primary)); color: #fff; text-align: left; padding: 8px 12px; font-size: 14px; font-weight: 500; }
        td { border: 1px solid #e5e7eb; padding: 8px 12px; font-size: 14px; color: #1f2937; }
        .bg-primary { background: hsl(var(--primary)) !important; color: #fff !important; }
        .pay-slip-net-card { display: block; margin-top: 16px; border: 1px solid #e5e7eb; }
        .pay-slip-signature-footer { display: none !important; }
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head><body><div class="pay-slip-print">${clone.innerHTML}</div></body></html>`)
    doc.close()

    const cleanup = () => {
      iframe.remove()
    }

    const runPrint = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } finally {
        setTimeout(cleanup, 400)
      }
    }

    const images = Array.from(doc.images)
    const pending = images.filter((img) => !img.complete)
    if (pending.length === 0) {
      setTimeout(runPrint, 200)
      return
    }
    let left = pending.length
    pending.forEach((img) => {
      const done = () => {
        left -= 1
        if (left <= 0) setTimeout(runPrint, 150)
      }
      img.addEventListener('load', done, { once: true })
      img.addEventListener('error', done, { once: true })
    })
  }

  const showContent = !loading && data

  const monthYearLabel = data ? formatMonthYearLabel(data.salaryMonthYear) : ''
  const basic = Number(data?.basicSalary ?? (data as any)?.BasicSalary ?? 0)
  const housing = Number(data?.housingAllowance ?? (data as any)?.HousingAllowance ?? 0)
  const transport = Number(
    data?.transpotationAllowance ??
      (data as any)?.TranspotationAllowance ??
      data?.transportationAllowance ??
      (data as any)?.TransportationAllowance ??
      0
  )
  const meal = Number(data?.mealAllowance ?? (data as any)?.MealAllowance ?? 0)
  const otAmount = Number(data?.otAmount ?? (data as any)?.OtAmount ?? 0)
  const bonus = Number(data?.monthlyBonus ?? (data as any)?.MonthlyBonus ?? 0)
  const incentive = Number(data?.monthlyIncentive ?? (data as any)?.MonthlyIncentive ?? 0)
  const otherAdditions = Number(data?.otherAllowance ?? (data as any)?.OtherAllowance ?? 0)
  const gosi = Number(data?.gosiDeduction ?? (data as any)?.GosiDeduction ?? 0)
  const absentDed = Number(data?.absentDeduction ?? (data as any)?.AbsentDeduction ?? 0)
  const lateDed = Number(data?.lateDeduction ?? (data as any)?.LateDeduction ?? 0)
  const earlyOutDed = Number(data?.earlyOutDeduction ?? (data as any)?.EarlyOutDeduction ?? 0)
  const loanDed = Number(data?.loanDeduction ?? (data as any)?.LoanDeduction ?? 0)
  const otherDed = Number(data?.otherDeductions ?? (data as any)?.OtherDeductions ?? 0)
  const totalDeduction = Number(
    data?.totalDeduction ??
      (data as any)?.TotalDeduction ??
      gosi + absentDed + lateDed + earlyOutDed + loanDed + otherDed
  )
  const netSalary = Number(
    data?.totalSalary ??
      (data as any)?.TotalSalary ??
      basic + housing + transport + meal + otAmount + bonus + incentive + otherAdditions - totalDeduction
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="bg-primary text-primary-foreground px-6 py-4 rounded-t-lg shrink-0 no-print">
          <DialogTitle className="text-white">Details | Salary Details</DialogTitle>
        </DialogHeader>

        <div ref={printRef} className="p-6 bg-white text-gray-900 print:block min-h-[200px] pay-slip-print">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          )}
          {!showContent && !loading && (
            <div className="py-16 text-center text-muted-foreground">No data to display.</div>
          )}
          {showContent && (
            <React.Fragment>
              {/* Header: same in modal and print — top-left date, center branch, right small logo; then payslip month; then header line */}
              <div className="pay-slip-header flex flex-col w-full">
                <div className="pay-slip-header-row flex justify-between items-center gap-4 mb-3 w-full">
                  <div className="pay-slip-date flex-shrink-0 text-left text-xs text-muted-foreground">
                    {new Date().toLocaleDateString('en-US', { month: 'numeric', day: '2-digit', year: '2-digit' })} {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                  <div className="pay-slip-branch flex-1 flex justify-center min-w-0">
                    <span className="text-sm font-semibold text-gray-800">{branchName}</span>
                  </div>
                  <div className="pay-slip-logo-wrap flex-shrink-0 flex justify-end">
                    <img src={getLogoPath()} alt="Logo" className="h-8 w-auto object-contain print:h-7" style={{ maxHeight: 32 }} />
                  </div>
                </div>
                <h2 className="pay-slip-title text-xl font-semibold text-center text-gray-900 mb-2">
                  Pay Slip for the month of {monthYearLabel}
                </h2>
                <div className="pay-slip-line h-0.5 bg-primary w-full mb-6" style={{ minHeight: 3 }} />
              </div>

              <div className="pay-slip-body grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Employee Information */}
                <div>
                  <table className="w-full border border-gray-200">
                <thead>
                  <tr>
                    <th colSpan={2} className={TABLE_HEADER_CLASS}>Employee Information</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={TABLE_CELL_CLASS}>Project</td><td className={TABLE_CELL_CLASS}>{empInfo?.project ?? data.project ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Department</td><td className={TABLE_CELL_CLASS}>{empInfo?.departmentName ?? data.department ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Employee #</td><td className={TABLE_CELL_CLASS}>{data.empCode ?? `EMP-${data.empId ?? '—'}`}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Employee Name</td><td className={TABLE_CELL_CLASS}>{empInfo?.employeeName ?? data.employeeName ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Reference</td><td className={TABLE_CELL_CLASS}>{empInfo?.reference ?? data.reference ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Joining Date</td><td className={TABLE_CELL_CLASS}>{formatJoiningDate(empInfo?.joiningDate ?? data.joiningDate)}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Nationality</td><td className={TABLE_CELL_CLASS}>{empInfo?.nationalityName ?? data.nationality ?? '—'}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Contract Salary</td><td className={TABLE_CELL_CLASS}>{formatNum(Number(data.contractSalary ?? (data as any).ContractSalary ?? 0))}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Pay Days</td><td className={TABLE_CELL_CLASS}>{data.payDays ?? '30'}</td></tr>
                </tbody>
                  </table>
                </div>

                {/* Earning Elements */}
                <div>
                  <table className="w-full border border-gray-200">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CLASS}>Earning Elements</th>
                    <th className={TABLE_HEADER_CLASS}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={TABLE_CELL_CLASS}>Basic</td><td className={TABLE_CELL_CLASS}>{formatNum(basic)}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Housing</td><td className={TABLE_CELL_CLASS}>{formatNum(housing)}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Transportation</td><td className={TABLE_CELL_CLASS}>{formatNum(transport)}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Food</td><td className={TABLE_CELL_CLASS}>{formatNum(meal)}</td></tr>
                  <tr><td className={TABLE_CELL_CLASS}>Overtime</td><td className={TABLE_CELL_CLASS}>{formatNum(otAmount)}</td></tr>
                  {bonus > 0 && (
                    <tr><td className={TABLE_CELL_CLASS}>Bonus</td><td className={TABLE_CELL_CLASS}>{formatNum(bonus)}</td></tr>
                  )}
                  {incentive > 0 && (
                    <tr><td className={TABLE_CELL_CLASS}>Incentive</td><td className={TABLE_CELL_CLASS}>{formatNum(incentive)}</td></tr>
                  )}
                  <tr><td className={TABLE_CELL_CLASS}>Other Earnings</td><td className={TABLE_CELL_CLASS}>{formatNum(otherAdditions)}</td></tr>
                </tbody>
              </table>

              <table className="w-full border border-gray-200 mt-4 pay-slip-deduction-table">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CLASS}>Deduction Elements</th>
                    <th className={TABLE_HEADER_CLASS}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className={TABLE_CELL_CLASS}>GOSI</td><td className={TABLE_CELL_CLASS}>{formatNum(gosi)}</td></tr>
                  {lateDed > 0 && (
                    <tr><td className={TABLE_CELL_CLASS}>Late Deduction</td><td className={TABLE_CELL_CLASS}>{formatNum(lateDed)}</td></tr>
                  )}
                  {earlyOutDed > 0 && (
                    <tr><td className={TABLE_CELL_CLASS}>Early Out Deduction</td><td className={TABLE_CELL_CLASS}>{formatNum(earlyOutDed)}</td></tr>
                  )}
                  {absentDed > 0 && (
                    <tr><td className={TABLE_CELL_CLASS}>Absence Deduction</td><td className={TABLE_CELL_CLASS}>{formatNum(absentDed)}</td></tr>
                  )}
                  {loanDed > 0 && (
                    <tr><td className={TABLE_CELL_CLASS}>Loan Deduction</td><td className={TABLE_CELL_CLASS}>{formatNum(loanDed)}</td></tr>
                  )}
                  <tr><td className={TABLE_CELL_CLASS}>Other Deductions</td><td className={TABLE_CELL_CLASS}>{formatNum(otherDed)}</td></tr>
                  <tr className="bg-primary text-primary-foreground"><td className="py-2 px-3 font-medium">Total Deduction</td><td className="py-2 px-3 font-medium">{formatNum(totalDeduction)}</td></tr>
                </tbody>
              </table>

              <div className="pay-slip-net-card mt-4 border border-gray-200 block">
                <div className="bg-primary text-primary-foreground py-2 px-3 font-medium">Net Payable Salary</div>
                <div className="py-2 px-3 text-lg font-semibold text-gray-900">{formatNum(netSalary)}</div>
                <div className="py-2 px-3 text-sm text-gray-700 border-t border-gray-200">Amount in Word: {amountInWords(netSalary)}</div>
              </div>
                </div>
              </div>

              {/* Signatures + Footer: hidden in modal, same layout in print */}
              <div className="pay-slip-signature-footer hidden">
                <div className="pay-slip-sig-row flex gap-12 mt-8 mb-6">
                  <div className="pay-slip-sig-item flex-1">
                    <div className="text-sm font-medium text-gray-700">Prepared by</div>
                    <div className="mt-1 min-h-[28px] border-b border-dashed border-gray-500" style={{ borderWidth: '1px' }} />
                  </div>
                  <div className="pay-slip-sig-item flex-1">
                    <div className="text-sm font-medium text-gray-700">Received by</div>
                    <div className="mt-1 min-h-[28px] border-b border-dashed border-gray-500" style={{ borderWidth: '1px' }} />
                  </div>
                </div>
                <div className="pay-slip-company text-center text-sm text-gray-600 border-t border-gray-200 pt-4">
                  S AND K Aerospace LLC Company - Tel: 9660555340017 C.R.: 1010315389
                </div>
              </div>
            </React.Fragment>
          )}
        </div>

        <DialogFooter className="border-t p-4 bg-gray-50 rounded-b-lg shrink-0 no-print">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} disabled={!data}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
